import { ApprovalSection } from "@/components/Approval";
import { useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap, type Schemas } from "@tern/api-client";
import { ArrowRight, ArrowLeft, GitBranch, MoreHorizontal, Plus, Trash2, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Comments } from "@/components/Comments";
import { PageHeader } from "@/components/AppShell";
import { CopyUidButton } from "@/components/CopyUidButton";
import { EmptyState } from "@/components/EmptyState";
import { IssueCreateDialog } from "@/components/IssueCreateDialog";
import { IssueRow } from "@/components/IssueRow";
import { Markdown, MarkdownEditor } from "@/components/Markdown";
import { Picker } from "@/components/Picker";
import { Avatar } from "@/components/PropertyIcons";
import { TaskCreateDialog } from "@/components/TaskCreateDialog";
import { HealthDot, ProgressRing, TaskDateButton, TaskLeadPicker, TaskMembersPicker, TaskMilestonePicker, TaskParentPicker, TaskPriorityPicker, TaskStatusIcon, TaskStatusPicker } from "@/components/TaskProps";
import { api, errorMessage } from "@/lib/api";
import { formatRelative, sortIssues } from "@/lib/issues";
import { deleteTask, liveIssues, liveTasks, patchTask, useProject, useProjectStore, type ProjectData, type Task } from "@/lib/store";
import { HEALTH_COLOR, ancestors, children, descendants, isBlocked, issuesOfTask, isDone, predecessors, successors, taskProgress } from "@/lib/tasks";
import { toast } from "@/lib/toast";
import { DEFAULT_VIEW } from "@/lib/views";
import { DocBackrefs } from "@/components/DocBackrefs";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/task/$id");
type TaskUpdate = Schemas["TaskUpdate"];

export function TaskDetailPage() {
  const { ws, id } = route.useParams();
  return <TaskById ws={ws} id={id} />;
}

/** Resolves the task's project through the API on first visit, then renders live from the store. */
export function TaskById({ ws, id, embedded }: { ws: string; id: string; embedded?: boolean }) {
  const { t } = useTranslation();
  const known = useProjectStore((s) => Object.values(s.byId).find((d) => d.tasks[id] && d.slug === ws));
  const fetched = useQuery({
    queryKey: ["task", id],
    queryFn: async () => unwrap(await api.GET("/api/tasks/{id}", { params: { path: { id } } })),
    enabled: !known,
    retry: false,
  });
  const projectId = known?.project.id ?? fetched.data?.project_id;
  const keyFromStore = useProjectStore((s) => (projectId ? s.byId[projectId]?.project.key : undefined));
  const projectsQ = useQuery({
    queryKey: ["projects", ws],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/projects", { params: { path: { slug: ws } } })),
    enabled: !keyFromStore && !!projectId,
  });
  const key = keyFromStore ?? projectsQ.data?.find((p) => p.id === projectId)?.key ?? "";
  useLoadProject(ws, key || "_");
  const d = useProject(ws, key);
  if (fetched.isError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={t("task.notFound")} body={t("task.notFoundBody")} actions={<Link to="/$ws/inbox" params={{ ws }} className="btn">{t("common.back")}</Link>} />
      </div>
    );
  }
  if (!d) return key ? <ProjectLoading ws={ws} kkey={key} /> : <div className="flex flex-1 items-center justify-center text-[13px] text-muted">{t("common.loading")}</div>;
  const task = d.tasks[id];
  if (!task || task.deleted_at) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={t("task.notFound")} body={t("task.notFoundBody")} actions={<Link to="/$ws/project/$key/tasks/$tab" params={{ ws, key, tab: "all" }} className="btn">{t("common.back")}</Link>} />
      </div>
    );
  }
  return <TaskView d={d} task={task} embedded={embedded} />;
}

function TaskView({ d, task, embedded }: { d: ProjectData; task: Task; embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canEdit = !!d.project.role;
  const [tab, setTab] = useState<"overview" | "issues">("overview");
  const [subOpen, setSubOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const prog = taskProgress(d, task);
  const kids = children(d, task.id);
  const preds = predecessors(d, task.id);
  const succs = successors(d, task.id);
  const chain = ancestors(d, task);
  const blocked = isBlocked(d, task.id);
  const issues = sortIssues(issuesOfTask(d, task.id), "priority");

  const remove = async () => {
    try {
      await deleteTask(task);
      toast(t("task.deleted", { name: task.name }));
      await navigate({ to: "/$ws/project/$key/tasks/$tab", params: { ws: d.slug, key: d.project.key, tab: "all" } });
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };

  return (
    <>
      <PageHeader
        besideCrumbs={<CopyUidButton uid={task.id} />}
        embedded={embedded}
        crumbs={[
          <Link key="p" to="/$ws/project/$key/tasks/$tab" params={{ ws: d.slug, key: d.project.key, tab: "all" }} className="hover:text-fg">{d.project.name}</Link>,
          ...chain.map((a) => <Link key={a.id} to="/$ws/task/$id" params={{ ws: d.slug, id: a.id }} className="hover:text-fg">{a.name}</Link>),
          <span key="t">{task.name}</span>,
        ]}
        right={
          canEdit ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild><button className="btn btn-ghost h-7 w-7 justify-center p-0"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={4} className="z-50 w-[200px] rounded-lg border border-border bg-elevated p-1 shadow-xl">
                  <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] outline-none data-[highlighted]:bg-hover" onSelect={() => setSubOpen(true)}><Plus className="h-3.5 w-3.5" /> {t("task.addSubTask")}</DropdownMenu.Item>
                  <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] text-danger outline-none data-[highlighted]:bg-hover" onSelect={() => void remove()}><Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : null
        }
      />
      <div className="flex h-10 items-center gap-1 border-b border-border px-3">
        {(["overview", "issues"] as const).map((k) => (
          <button key={k} className={`rounded-md px-2.5 py-1 text-[13px] ${tab === k ? "bg-active text-fg" : "text-muted hover:bg-hover hover:text-fg"}`} onClick={() => setTab(k)}>
            {t(`task.tabs.${k}`)} {k === "issues" && <span className="text-faint">{issues.length}</span>}
          </button>
        ))}
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {tab === "overview" ? (
            <div className="mx-auto max-w-[760px] px-8 py-6">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-8 w-1.5 rounded" style={{ background: task.color ?? "var(--accent)" }} />
                <div className="min-w-0 flex-1">
                  <NameEditor task={task} canEdit={canEdit} />
                  <SummaryEditor task={task} canEdit={canEdit} />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-1.5">
                <TaskStatusPicker task={task} />
                <TaskPriorityPicker task={task} />
                <TaskLeadPicker d={d} task={task} />
                <TaskDateButton task={task} field="start_date" />
                <span className="text-faint">→</span>
                <TaskDateButton task={task} field="target_date" />
                <TaskMilestonePicker d={d} task={task} />
                {blocked && <span className="rounded-md bg-warning/20 px-2 py-0.5 text-[12px] text-warning">{t("task.blockedBy", { n: preds.filter((p) => !isDone(p.task)).length })}</span>}
              </div>
              <DescriptionEditor task={task} canEdit={canEdit} />

              <RelationsCard d={d} task={task} kids={kids} preds={preds} succs={succs} canEdit={canEdit} onAddSub={() => setSubOpen(true)} />

              <Updates d={d} task={task} canEdit={canEdit} />
              <Comments d={d} entityType="task" entityId={task.id} />
              <Activity d={d} task={task} />
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-[12.5px] text-muted">{t("task.issuesInTask", { n: issues.length })}</span>
                {canEdit && (
                  <div className="flex gap-1">
                    <AddExistingIssue d={d} task={task} />
                    <button className="btn h-7 text-[12.5px]" onClick={() => setIssueOpen(true)}><Plus className="h-3.5 w-3.5" /> {t("nav.newIssue")}</button>
                  </div>
                )}
              </div>
              {issues.length === 0 ? (
                <div className="flex flex-1 items-center justify-center p-10"><EmptyState title={t("task.noIssuesTitle")} body={t("task.noIssuesBody")} /></div>
              ) : (
                issues.map((i) => <IssueRow key={i.id} d={d} issue={i} view={DEFAULT_VIEW} />)
              )}
            </div>
          )}
        </div>
        <aside className="w-[300px] shrink-0 overflow-y-auto border-l border-border p-4">
          <ApprovalSection d={d} item={task} status={task.status} onChange={(config) => patchTask(task, config)} onApprove={() => patchTask(task, { status: "completed" })} />
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between text-[12px] text-muted"><span>{t("task.progress")}</span><span>{prog ? `${Math.round(prog.ratio * 100)}%` : "—"}</span></div>
            <div className="h-1.5 overflow-hidden rounded-full bg-active"><div className="h-full rounded-full bg-accent" style={{ width: `${Math.round((prog?.ratio ?? 0) * 100)}%` }} /></div>
            {prog && <div className="mt-1 text-[11.5px] text-faint">{t("task.progressDetail", { done: prog.completed, started: prog.started, total: prog.total })}</div>}
          </div>
          <Prop label={t("task.health")}><HealthDot health={d.health[task.id]?.health} /> {!d.health[task.id] && <span className="text-[12px] text-faint">—</span>}</Prop>
          <Prop label={t("task.lead")}><TaskLeadPicker d={d} task={task} /></Prop>
          <Prop label={t("task.members")}><TaskMembersPicker d={d} task={task} /></Prop>
          <Prop label={t("nav.milestone")}><TaskMilestonePicker d={d} task={task} /></Prop>
          <Prop label={t("task.parent")}><TaskParentPicker d={d} task={task} /></Prop>
          <DocBackrefs d={d} entity={{ type: "task", id: task.id }} />
          <div className="mt-4 border-t border-border pt-3 text-[11.5px] text-faint">
            <div>{t("issue.updated", { when: formatRelative(task.updated_at, i18n.language) })}</div>
          </div>
        </aside>
      </div>
      <TaskCreateDialog d={d} open={subOpen} onOpenChange={setSubOpen} defaults={{ parent_id: task.id, milestone_id: task.milestone_id ?? undefined }} />
      <IssueCreateDialog d={d} open={issueOpen} onOpenChange={setIssueOpen} defaults={{ task_id: task.id }} />
    </>
  );
}

function Prop({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 flex min-h-[28px] items-start gap-2">
      <span className="w-[84px] shrink-0 pt-1 text-[12px] text-muted">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Parent · sub-tasks · predecessors · successors — the "at a glance" relationship card. */
function RelationsCard({ d, task, kids, preds, succs, canEdit, onAddSub }: { d: ProjectData; task: Task; kids: Task[]; preds: ReturnType<typeof predecessors>; succs: ReturnType<typeof successors>; canEdit: boolean; onAddSub: () => void }) {
  const { t } = useTranslation();
  const parent = task.parent_id ? d.tasks[task.parent_id] : undefined;
  const forbidden = new Set([task.id, ...descendants(d, task.id).map((x) => x.id), ...ancestors(d, task).map((x) => x.id)]);
  const candidates = liveTasks(d).filter((x) => !forbidden.has(x.id));
  const add = async (otherId: string, direction: "predecessor" | "successor") => {
    try {
      const dep = unwrap(await api.POST("/api/tasks/{id}/dependencies", { params: { path: { id: task.id } }, body: { other_id: otherId, direction } }));
      useProjectStore.getState().applyDependency(dep, "created");
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const remove = async (depId: string) => {
    try {
      const dep = unwrap(await api.DELETE("/api/task-dependencies/{id}", { params: { path: { id: depId } } }));
      useProjectStore.getState().applyDependency(dep, "deleted");
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const empty = (k: string) => <div className="px-2 py-2 text-[12px] text-faint">{t(k)}</div>;
  return (
    <section className="mt-8">
      <h3 className="mb-2 text-[13px] font-semibold text-muted">{t("task.relations")}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <Block title={t("task.parentAndSubs")} icon={<GitBranch className="h-3.5 w-3.5" />} action={canEdit && <button className="text-[11.5px] text-muted hover:text-fg" onClick={onAddSub}>+ {t("task.addSubTask")}</button>}
          items={
            <ul className="divide-y divide-border/60">
              {parent && (
                <li className="flex h-8 items-center gap-2 px-2 text-[12.5px] text-muted">
                  <span className="text-faint">↑ {t("task.parent")}</span>
                  <TaskStatusIcon status={parent.status} className="h-3 w-3" />
                  <Link to="/$ws/task/$id" params={{ ws: d.slug, id: parent.id }} className="truncate hover:underline">{parent.name}</Link>
                </li>
              )}
              {kids.map((k) => <TaskLine key={k.id} d={d} x={k} />)}
              {!parent && kids.length === 0 && empty("task.noSubTasks")}
            </ul>
          } />
        <div className="flex flex-col gap-3">
          <Block title={t("task.predecessors")} icon={<ArrowLeft className="h-3.5 w-3.5" />}
            action={canEdit && <Picker value={null} items={candidates.filter((x) => !preds.some((p) => p.task.id === x.id)).map((x) => ({ id: x.id, label: x.name, icon: <TaskStatusIcon status={x.status} className="h-3 w-3" /> }))} onSelect={(id) => void add(id, "predecessor")} placeholder={t("task.addPredecessor")} trigger={<button className="text-[11.5px] text-muted hover:text-fg">+ {t("task.addPredecessor")}</button>} />}
            items={<ul className="divide-y divide-border/60">{preds.map((p) => <TaskLine key={p.dep.id} d={d} x={p.task} onRemove={canEdit ? () => void remove(p.dep.id) : undefined} />)}{preds.length === 0 && empty("task.noPredecessors")}</ul>} />
          <Block title={t("task.successors")} icon={<ArrowRight className="h-3.5 w-3.5" />}
            action={canEdit && <Picker value={null} items={candidates.filter((x) => !succs.some((s) => s.task.id === x.id)).map((x) => ({ id: x.id, label: x.name, icon: <TaskStatusIcon status={x.status} className="h-3 w-3" /> }))} onSelect={(id) => void add(id, "successor")} placeholder={t("task.addSuccessor")} trigger={<button className="text-[11.5px] text-muted hover:text-fg">+ {t("task.addSuccessor")}</button>} />}
            items={<ul className="divide-y divide-border/60">{succs.map((s) => <TaskLine key={s.dep.id} d={d} x={s.task} onRemove={canEdit ? () => void remove(s.dep.id) : undefined} />)}{succs.length === 0 && empty("task.noSuccessors")}</ul>} />
        </div>
      </div>
    </section>
  );
}

function TaskLine({ d, x, onRemove }: { d: ProjectData; x: Task; onRemove?: () => void }) {
  const { t } = useTranslation();
  const p = taskProgress(d, x);
  return (
    <li className="group flex h-8 items-center gap-2 px-2 text-[13px]">
      <TaskStatusIcon status={x.status} />
      <Link to="/$ws/task/$id" params={{ ws: d.slug, id: x.id }} className="min-w-0 flex-1 truncate hover:underline">{x.name}</Link>
      {isBlocked(d, x.id) && <span className="rounded bg-warning/20 px-1 text-[10.5px] text-warning">{t("task.blocked")}</span>}
      <span className="flex items-center gap-1 text-[11.5px] text-muted"><ProgressRing ratio={p?.ratio ?? null} size={13} />{p ? `${Math.round(p.ratio * 100)}%` : "—"}</span>
      <Avatar user={x.lead_id ? d.users[x.lead_id] : undefined} size={16} />
      {onRemove && <button className="opacity-0 group-hover:opacity-100" onClick={onRemove}><X className="h-3 w-3 text-muted" /></button>}
    </li>
  );
}

function Block({ title, icon, items, action }: { title: string; icon: React.ReactNode; items: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border">
      <div className="flex h-8 items-center gap-1.5 border-b border-border bg-sidebar/60 px-2 text-[12px] font-medium text-muted">
        {icon} {title} <span className="ml-auto">{action}</span>
      </div>
      {items}
    </div>
  );
}

function NameEditor({ task, canEdit }: { task: Task; canEdit: boolean }) {
  const [value, setValue] = useState(task.name);
  const [seen, setSeen] = useState(task.name);
  if (seen !== task.name) {
    setSeen(task.name);
    setValue(task.name);
  }
  return (
    <input className="w-full bg-transparent text-[22px] font-semibold text-fg outline-none" value={value} readOnly={!canEdit} onChange={(e) => setValue(e.target.value)}
      onBlur={() => { const v = value.trim(); if (v && v !== task.name) void patchTask(task, { name: v }).catch((e) => toast(errorMessage(e), { tone: "error" })); else setValue(task.name); }}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
  );
}

function SummaryEditor({ task, canEdit }: { task: Task; canEdit: boolean }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(task.summary);
  const [seen, setSeen] = useState(task.summary);
  if (seen !== task.summary) {
    setSeen(task.summary);
    setValue(task.summary);
  }
  return (
    <input className="mt-0.5 w-full bg-transparent text-[13px] text-muted outline-none placeholder:text-faint" placeholder={canEdit ? t("task.summaryPlaceholder") : ""} value={value} readOnly={!canEdit} onChange={(e) => setValue(e.target.value)}
      onBlur={() => value !== task.summary && void patchTask(task, { summary: value }).catch((e) => toast(errorMessage(e), { tone: "error" }))} />
  );
}

function DescriptionEditor({ task, canEdit }: { task: Task; canEdit: boolean }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.description);
  const save = (markdown = draft) => {
    setEditing(false);
    if (markdown !== task.description) void patchTask(task, { description: markdown }).catch((e) => toast(errorMessage(e), { tone: "error" }));
  };
  if (editing) return <div className="mt-4"><MarkdownEditor projectId={task.project_id} value={draft} onChange={setDraft} onSave={save} saveOnBlur autoFocus minRows={6} placeholder={t("task.descriptionPlaceholder")} /></div>;
  return (
    <div className={`mt-4 min-h-[36px] rounded-md ${canEdit ? "-m-1 cursor-text p-1 hover:bg-hover/40" : ""}`} onClick={() => canEdit && (setDraft(task.description), setEditing(true))}>
      {task.description ? <Markdown source={task.description} /> : <span className="text-[13px] text-faint">{t("task.descriptionPlaceholder")}</span>}
    </div>
  );
}

function Updates({ d, task, canEdit }: { d: ProjectData; task: Task; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["task", task.id, "updates"], queryFn: async () => unwrap(await api.GET("/api/tasks/{id}/updates", { params: { path: { id: task.id } } })) });
  const [health, setHealth] = useState("on_track");
  const [body, setBody] = useState("");
  const [editorBusy, setEditorBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const post = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/tasks/{id}/updates", { params: { path: { id: task.id } }, body: { health, body } })),
    onSuccess: (u) => {
      useProjectStore.getState().applyHealth(u, "created");
      void qc.invalidateQueries({ queryKey: ["task", task.id, "updates"] });
      setBody("");
      setOpen(false);
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const del = useMutation({
    mutationFn: async (id: string) => unwrap(await api.DELETE("/api/task-updates/{id}", { params: { path: { id } } })),
    onSuccess: (u) => { useProjectStore.getState().applyHealth(u, "deleted"); void qc.invalidateQueries({ queryKey: ["task", task.id, "updates"] }); },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  return (
    <section className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-muted">{t("task.updates")}</h3>
        {canEdit && !open && <button className="btn btn-ghost h-7 text-[12.5px]" onClick={() => setOpen(true)}><Plus className="h-3.5 w-3.5" /> {t("task.postUpdate")}</button>}
      </div>
      {open && (
        <div className="mb-3 rounded-lg border border-border bg-elevated p-3">
          <div className="mb-2 flex gap-1">
            {(["on_track", "at_risk", "off_track"] as const).map((h) => (
              <button key={h} className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12.5px] ${health === h ? "border-border-strong bg-active text-fg" : "border-transparent text-muted hover:bg-hover"}`} onClick={() => setHealth(h)}>
                <span className="h-2 w-2 rounded-full" style={{ background: HEALTH_COLOR[h] }} /> {t(`health.${h}`)}
              </button>
            ))}
          </div>
          <MarkdownEditor projectId={task.project_id} value={body} onChange={setBody} onBusyChange={setEditorBusy} disabled={post.isPending} placeholder={t("task.updatePlaceholder")} minRows={3} onSave={() => !post.isPending && post.mutate()} />
          <div className="mt-2 flex justify-end gap-2">
            <button className="btn h-7 text-[12.5px]" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button className="btn btn-primary h-7 text-[12.5px]" disabled={post.isPending || editorBusy} onClick={() => post.mutate()}>{t("task.postUpdate")}</button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {(q.data ?? []).map((u: TaskUpdate) => (
          <div key={u.id} className="group rounded-lg border border-border p-3">
            <div className="mb-1 flex items-center gap-2 text-[12.5px]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: HEALTH_COLOR[u.health] }} />
              <span className="font-medium">{t(`health.${u.health}`)}</span>
              <Avatar user={d.users[u.author_id]} size={16} />
              <span className="text-muted">{d.users[u.author_id]?.name}</span>
              <span className="text-faint">{formatRelative(u.created_at, i18n.language)}</span>
              {canEdit && <button className="ml-auto text-faint opacity-0 hover:text-danger group-hover:opacity-100" onClick={() => del.mutate(u.id)}><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
            {u.body && <Markdown source={u.body} />}
          </div>
        ))}
        {q.data?.length === 0 && !open && <p className="text-[12.5px] text-faint">{t("task.noUpdates")}</p>}
      </div>
    </section>
  );
}

function Activity({ d, task }: { d: ProjectData; task: Task }) {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["task", task.id, "activities"], queryFn: async () => unwrap(await api.GET("/api/tasks/{id}/activities", { params: { path: { id: task.id } } })) });
  const label = (a: Schemas["Activity"]) => {
    if (a.action === "created") return t("activity.createdTask");
    if (a.action === "deleted") return t("activity.deletedTask");
    if (a.action === "update_posted") return t("activity.postedUpdate", { health: t(`health.${a.field}`) });
    if (a.action === "dependency_added") return t("activity.dependencyAdded", { dir: t(`task.${a.field === "predecessor" ? "predecessors" : "successors"}`) });
    if (a.field === "approval_required") return `${t("activity.changed", { field: t("approval.settings") })} → ${t(a.to_value ? "approval.required" : "approval.none")}`;
    if (a.field === "approvers") return `${t("activity.changed", { field: t("approval.select") })} → ${Array.isArray(a.to_value) ? a.to_value.map((id) => d.users[String(id)]?.name ?? t("approval.formerMember")).join(", ") : "—"}`;
    if (a.field === "approved_by") return t(a.to_value ? "approval.activityApproved" : "approval.activityReset");
    if (a.field === "status") return `${t("activity.changed", { field: t("issue.status") })} ${t(`taskStatus.${String(a.from_value)}`, { defaultValue: "—" })} → ${t(`taskStatus.${String(a.to_value)}`, { defaultValue: "—" })}`;
    if (a.field === "lead") return `${t("activity.changed", { field: t("task.lead") })} → ${a.to_value ? d.users[String(a.to_value)]?.name ?? "?" : t("task.noLead")}`;
    if (a.field === "parent") return `${t("activity.changed", { field: t("task.parent") })} → ${a.to_value ? d.tasks[String(a.to_value)]?.name ?? "?" : t("task.noParent")}`;
    if (a.field === "milestone") return `${t("activity.changed", { field: t("nav.milestone") })} → ${a.to_value ? d.milestones[String(a.to_value)]?.name ?? "?" : "—"}`;
    if (a.field === "description" || a.field === "summary") return t("activity.editedDescription");
    if (a.field === "name") return t("activity.renamed", { to: String(a.to_value) });
    return t("activity.changed", { field: t(`task.${a.field}`, { defaultValue: a.field ?? "" }) });
  };
  return (
    <section className="mt-8">
      <h3 className="mb-2 text-[13px] font-semibold text-muted">{t("issue.activity")}</h3>
      <div className="flex flex-col gap-1.5">
        {(q.data ?? []).filter(a => a.action !== "commented").map((a) => (
          <div key={a.id} className="flex items-center gap-2 pl-1 text-[12.5px] text-muted">
            <Avatar user={d.users[a.actor_id]} size={16} />
            <span className="font-medium text-fg">{d.users[a.actor_id]?.name ?? "?"}</span>
            <span className="min-w-0 truncate">{label(a)}</span>
            <span className="ml-auto shrink-0 text-[11.5px] text-faint">{formatRelative(a.created_at, i18n.language)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function AddExistingIssue({ d, task }: { d: ProjectData; task: Task }) {
  const { t } = useTranslation();
  const candidates = liveIssues(d).filter((i) => i.task_id !== task.id);
  return (
    <Picker
      value={null}
      placeholder={t("task.addExistingIssue")}
      items={candidates.map((i) => ({ id: i.id, label: `${d.project.key}-${i.number} ${i.title}` }))}
      onSelect={(id) => {
        const issue = d.issues[id];
        if (issue) void import("@/lib/store").then((m) => m.patchIssue(issue, { task_id: task.id })).catch((e) => toast(errorMessage(e), { tone: "error" }));
      }}
      trigger={<button className="btn h-7 text-[12.5px]">{t("task.addExistingIssue")}</button>}
    />
  );
}
